import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useSuiClient } from "@mysten/dapp-kit";
import type { SuiJsonRpcClient } from "@mysten/sui/jsonRpc";
import { readPolicyObject, type PolicyObject } from "@altheia-xyz/sui";
import { ChainBadge } from "@/components/ChainBadge";
import type { AgentObject } from "@/lib/api";

/**
 * Sui agents list. Full roster with a status filter. Same Persian + Orange bar
 * as the fleet dashboard: ruled table, hairline tabs, mono data, serif title.
 * Status is read live from the on-chain Policy (revoked/paused), same as the
 * fleet — the backend row stays "active" since SDK revoke writes no backend tx.
 */

type Status = "active" | "paused" | "revoked";
type Filter = "all" | Status;

function statusOf(a: AgentObject, chainPol?: PolicyObject): Status {
  if (chainPol?.revoked || a.status === "revoked" || a.revoked_at) return "revoked";
  if (chainPol?.paused || a.status === "paused") return "paused";
  return "active";
}

function short(id: string | null | undefined, head = 6, tail = 4): string {
  if (!id) return "—";
  return id.length <= head + tail + 1 ? id : `${id.slice(0, head)}…${id.slice(-tail)}`;
}

function capLabel(a: AgentObject): string {
  const pol = (a.policy ?? {}) as Record<string, unknown>;
  const caps = (pol.per_asset_caps ?? pol.caps) as
    | Array<{ asset?: string; per_day?: number | string }>
    | undefined;
  const primary = caps?.[0];
  const day = Number(primary?.per_day ?? 0);
  return day > 0 ? `${day} ${primary?.asset ?? "SUI"}/day` : "no cap";
}

function frameworkLabel(f?: string): string {
  switch (f) {
    case "sak": return "Solana Agent Kit";
    case "mcp": return "MCP";
    case "eliza": return "ElizaOS";
    case "virtuals": return "Virtuals";
    case "griffain": return "Griffain";
    case "custom": return "Custom";
    default: return f ?? "custom";
  }
}

const FILTERS: Filter[] = ["all", "active", "paused", "revoked"];

export function SuiAgentsPage({ agents }: { agents: AgentObject[] }) {
  const [filter, setFilter] = useState<Filter>("all");
  const client = useSuiClient() as unknown as SuiJsonRpcClient;
  const [chainPols, setChainPols] = useState<Record<string, PolicyObject>>({});

  useEffect(() => {
    const withPol = agents.filter((a) => a.sui_policy_id);
    if (withPol.length === 0) return;
    let cancelled = false;
    Promise.allSettled(withPol.map((a) => readPolicyObject(client, a.sui_policy_id!).then((p) => [a.id, p] as const)))
      .then((rs) => {
        if (cancelled) return;
        const m: Record<string, PolicyObject> = {};
        for (const r of rs) if (r.status === "fulfilled") m[r.value[0]] = r.value[1];
        setChainPols(m);
      });
    return () => { cancelled = true; };
  }, [agents, client]);

  const counts = useMemo(() => {
    const c: Record<Filter, number> = { all: agents.length, active: 0, paused: 0, revoked: 0 };
    for (const a of agents) c[statusOf(a, chainPols[a.id])] += 1;
    return c;
  }, [agents, chainPols]);

  const rows = useMemo(
    () => agents.filter((a) => filter === "all" || statusOf(a, chainPols[a.id]) === filter),
    [agents, filter, chainPols],
  );

  return (
    <div className="sa">
      <header className="sa__head">
        <div>
          <h1 className="sa__title">Agents</h1>
          <p className="sa__sub">sui · {agents.length} provisioned on-chain</p>
        </div>
        <Link to="/sui/agents/new" className="sa__new">
          Create agent
        </Link>
      </header>

      <div className="sa__tabs" role="tablist" aria-label="filter by status">
        {FILTERS.map((f) => (
          <button
            key={f}
            role="tab"
            aria-selected={filter === f}
            className={filter === f ? "on" : ""}
            onClick={() => setFilter(f)}
          >
            {f}
            <span className="sa__tab-count">{counts[f]}</span>
          </button>
        ))}
      </div>

      {rows.length === 0 ? (
        <div className="sa__none">No {filter === "all" ? "" : filter} agents.</div>
      ) : (
        <div className="sa__tablewrap">
          <table className="sa__table">
            <thead>
              <tr>
                <th>Agent</th>
                <th>Status</th>
                <th>Framework</th>
                <th className="r">Cap</th>
                <th>Vault</th>
                <th>Policy</th>
                <th className="r"></th>
              </tr>
            </thead>
            <tbody>
              {rows.map((a) => {
                const status = statusOf(a, chainPols[a.id]);
                return (
                  <tr key={a.id} className={status === "revoked" ? "is-revoked" : ""}>
                    <td>
                      <Link to={`/sui/agents/${a.id}`} className="sa__name">{a.name}</Link>
                      <span className="sa__chain"><ChainBadge chain="sui" /></span>
                    </td>
                    <td>
                      <span className={`sa__status sa__status--${status}`}>
                        <span className="sa__dot" />
                        {status}
                      </span>
                    </td>
                    <td className="sa__fw">{frameworkLabel(a.framework)}</td>
                    <td className="r sa__cap">{capLabel(a)}</td>
                    <td className="sa__mono">{short(a.sui_vault_id)}</td>
                    <td className="sa__mono">{short(a.sui_policy_id)}</td>
                    <td className="r">
                      <Link to={`/sui/agents/${a.id}`} className="sa__open">View</Link>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      <style>{`
        .sa { max-width: var(--page-max); margin: 0 auto; font-family: var(--font-body); color: var(--bone); }
        .sa__head { display: flex; align-items: flex-end; justify-content: space-between; gap: var(--s-4); margin-bottom: var(--s-6); }
        .sa__title { font: var(--type-display-page); font-style: italic; margin: 0; }
        .sa__sub { font: var(--type-mono-data); color: var(--slate-2); margin: var(--s-2) 0 0; }
        .sa__new { font: var(--type-label); letter-spacing: var(--tracking-label); text-transform: uppercase; background: var(--indigo); color: var(--text-on-ink); padding: var(--s-3) var(--s-5); border-radius: var(--r-sm); white-space: nowrap; transition: background var(--dur-snap) var(--ease-snap); }
        .sa__new:hover { background: var(--indigo-deep); }

        .sa__tabs { display: inline-flex; border: 1px solid var(--line); border-radius: var(--r-sm); overflow: hidden; margin-bottom: var(--s-5); }
        .sa__tabs button { display: inline-flex; align-items: center; gap: 7px; font: var(--type-mono-address); text-transform: uppercase; letter-spacing: 0.06em; padding: var(--s-2) var(--s-4); border-right: 1px solid var(--line); background: var(--bg); color: var(--slate); transition: color var(--dur-snap) var(--ease-snap); }
        .sa__tabs button:last-child { border-right: 0; }
        .sa__tabs button:hover { color: var(--bone); }
        .sa__tabs button.on { background: var(--bone); color: var(--text-on-ink); }
        .sa__tab-count { font-variant-numeric: tabular-nums; color: var(--slate-3); }
        .sa__tabs button.on .sa__tab-count { color: var(--text-on-ink); opacity: 0.7; }

        .sa__tablewrap { overflow-x: auto; }
        .sa__table { width: 100%; border-collapse: collapse; }
        .sa__table th { font: var(--type-mono-address); text-transform: uppercase; letter-spacing: 0.08em; color: var(--slate-3); text-align: left; font-weight: 400; padding: 0 var(--s-4) var(--s-3) 0; border-bottom: 1px solid var(--line); white-space: nowrap; }
        .sa__table th.r, .sa__table td.r { text-align: right; padding-right: 0; }
        .sa__table td { font: var(--type-mono-data); padding: var(--s-4) var(--s-4) var(--s-4) 0; border-bottom: 1px solid var(--line-soft); vertical-align: middle; }
        .sa__table tbody tr { transition: background var(--dur-snap) var(--ease-snap); }
        .sa__table tbody tr:hover { background: var(--bg-elev); }
        .sa__table tbody tr.is-revoked { background: var(--bg-revoked); }
        .sa__table tbody tr.is-revoked td { color: var(--slate-3); }

        .sa__name { font: var(--type-body); color: var(--bone); font-weight: 500; }
        .sa__name:hover { color: var(--accent-deep); }
        .sa__chain { margin-left: var(--s-2); position: relative; top: -1px; }
        tr.is-revoked .sa__name { color: var(--slate-2); }

        .sa__status { display: inline-flex; align-items: center; gap: 7px; font: var(--type-kicker); letter-spacing: var(--tracking-kicker); text-transform: uppercase; }
        .sa__dot { width: 8px; height: 8px; border-radius: 50%; flex: none; }
        .sa__status--active { color: var(--ok); }
        .sa__status--active .sa__dot { background: var(--accent); animation: ds-pulse var(--dur-pulse) ease-in-out infinite; }
        .sa__status--paused { color: var(--slate); }
        .sa__status--paused .sa__dot { background: var(--slate); }
        .sa__status--revoked { color: var(--oxblood); }
        .sa__status--revoked .sa__dot { background: var(--oxblood); }

        .sa__fw { color: var(--slate); }
        .sa__cap { color: var(--bone-2); white-space: nowrap; }
        .sa__mono { font: var(--type-mono-address); color: var(--slate-2); }
        .sa__open { font: var(--type-mono-address); text-transform: uppercase; letter-spacing: 0.08em; color: var(--slate); }
        .sa__open:hover { color: var(--accent-deep); }
        .sa__none { font: var(--type-body); color: var(--slate-2); padding: var(--s-7) 0; }

        @media (max-width: 768px) {
          .sa__head { flex-direction: column; align-items: flex-start; }
          .sa__tabs { width: 100%; }
          .sa__tabs button { flex: 1; justify-content: center; }
        }
        @media (prefers-reduced-motion: reduce) {
          .sa__dot { animation: none !important; }
        }
      `}</style>
    </div>
  );
}
